// The shared office modal shell (Session ACW loop 4, extracted from the private shell the
// subcontractor dialogs introduced in the subcontractor patch). Every office modal renders
// through this so the mobile rules live in ONE place:
//
//  - createPortal to document.body: a modal invoked from inside a .ele-list-table <td> must
//    never inherit the cell's `white-space: nowrap` (the original overflow bug), and its
//    fixed positioning must be relative to the viewport, not a transformed ancestor.
//  - The overlay keeps a 16px inset (p-4) on every side -- a modal is never full-bleed on a
//    phone -- and scrolls (overflow-y-auto) so a form taller than a phone viewport is always
//    reachable instead of clipping top and bottom.
//  - The panel centres with my-auto (auto margins collapse to 0 once the content is taller
//    than the viewport, so tall content top-aligns and scrolls; short content is centred
//    exactly as the old items-center overlays were). Top-anchored dialogs pass their own
//    margin (e.g. "my-16") instead.
//  - Width: mobile is always w-full inside the p-4 inset; desktop is capped by the `width`
//    max-w-* class, ceiling max-w-[560px] (the Session ACW desktop ceiling for office modals).
//  - z-50 sits above the mobile nav drawer (z-40), the sticky header (z-20) and the search
//    dropdown (z-30).
//  - Escape closes when the caller passes onClose (callers guard their own busy state, the
//    ConfirmDeleteModal convention), and focus moves INTO the dialog on open (the aria-modal
//    contract, mirroring the OfficeLayout drawer fix) -- unless the modal already focused one
//    of its own inputs (autoFocus), which we never steal from.
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

export function ModalShell({
  ariaLabel,
  onClose,
  closeOnBackdrop = false,
  width = "max-w-[480px]",
  border = "border-ele-purple-accent",
  margin = "my-auto",
  scrim = "bg-black/60",
  panelClassName = "p-6",
  children,
}: {
  ariaLabel: string;
  // Escape-to-close (and, with closeOnBackdrop, backdrop-click-to-close). The caller guards its
  // own in-flight state: pass e.g. () => !busy && close(). Omit to disable both close paths.
  onClose?: () => void;
  closeOnBackdrop?: boolean;
  // A max-w-* class. Keep a modal's existing width when it is <= 560px; anything wider is
  // clamped to max-w-[560px] (the locked desktop ceiling for migrated office modals).
  width?: string;
  // The panel border colour class (e.g. border-ele-danger for destructive dialogs).
  border?: string;
  // Vertical placement: my-auto centres; a top-anchored dialog passes its own my-* instead.
  margin?: string;
  // The overlay wash. bg-black/60 platform-wide; a rare heavier wash passes bg-black/70. The
  // scrim is deliberately NOT themed (Session AFK): a dimmed backdrop is standard overlay
  // behaviour in both light and dark, and the panel above it carries the themed surface.
  scrim?: string;
  // Padding plus any panel extras (shadow, internal flex/max-h scroll). Minimum 20px vertical
  // padding (p-5 / p-6); a legacy p-4 panel keeps desktop pixels and adds max-sm:p-5.
  panelClassName?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus lands on the panel when the dialog opens, so a keyboard or screen-reader user is
  // inside the dialog they just invoked. If the modal already claimed focus itself (an
  // autoFocus input), leave it alone.
  useEffect(() => {
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
  }, []);

  // Esc closes (the platform's modal convention -- see ConfirmDeleteModal). The caller's
  // onClose guards its own busy state, so a save in flight is never abandoned mid-request.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The overlay re-applies the .office-ui scope: the portal target (document.body) sits OUTSIDE
  // the OfficeLayout wrapper that carries it, and without the class a portaled modal would lose
  // the Session ACG office design system (13.5px base, the quiet-hairline --ele-purple-accent /
  // --ele-divider overrides) and the loop 4 mobile input-height rule. text-ele-body restores the
  // inherited text colour the layout wrapper provided. Every ModalShell consumer is an office
  // surface, so re-scoping here is always correct.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className={`office-ui fixed inset-0 z-50 flex justify-center overflow-y-auto whitespace-normal p-4 text-ele-body ${scrim}`}
      // Session ALE: keep the bottom gutter clear of the home indicator. index.html sets
      // viewport-fit=cover and the OFFICE app is itself an installable standalone PWA
      // ("ELE-Guvnor"), so in an installed app on a notched phone the overlay runs edge to edge and
      // the last footer button -- which MODAL_ACTIONS stacks to the bottom on a phone -- sits under
      // the indicator. max() rather than the bare env() used by EngineerLayout and PortalShell,
      // because those pad an element with no base padding of its own: here a bare env() would
      // OVERRIDE the p-4 above and collapse the gutter to zero on every non-notched device. On a
      // desktop env() is 0, so this resolves to exactly the 1rem p-4 already gave: no pixel moves.
      style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      onClick={closeOnBackdrop && onClose ? () => onClose() : undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`ele-modal-in h-fit w-full rounded-ele border bg-[var(--ele-shell)] outline-none ${border} ${width} ${margin} ${panelClassName}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

// The mobile action-row contract (Session ACW loop 4c). Footers with 2+ buttons keep today's
// inline right-aligned row on sm+ and stack full-width below it; the primary action stays LAST
// in the DOM so flex-col-reverse puts it on top visually on a phone. Compose as:
//   <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}> ... <button className={`... ${MODAL_ACTION_BTN}`}>
// (each footer keeps its own mt-* / gap-* so desktop stays pixel-identical).
//
// Session ATP: sm:flex-wrap added. The panel this row sits in (below) carries no overflow
// clipping of its own -- by design, so a focus ring or a dropdown a footer button opens is never
// cut off -- so on sm+ (a plain row, no wrap) a footer with three or more real-width buttons
// (e.g. the Scheduling entry modal's Pause (awaiting parts) / Cancel visit / Delete visit / Close,
// the widest label set this row carries) could exceed the panel's content width at viewports just
// past the sm breakpoint. With justify-end and no wrap, that overflow pushed the row's LEADING
// button (Pause, in that case) past the panel's left edge, rendering it outside the card. Letting
// the row wrap onto a second line, still right-aligned per line, fixes this at the root: every
// button stays visually inside the card at every width, and nothing needed a clipping wrapper
// that would also have caught a legitimate focus ring or open menu.
export const MODAL_ACTIONS = "flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end";
export const MODAL_ACTION_BTN = "max-sm:w-full max-sm:min-h-11";

// Session ATU: the primary and secondary footer buttons as ONE string each, so a footer cannot be
// written without the radius, padding and font again. The Standby rota page's two modals were: their
// footers carried MODAL_ACTION_BTN alone, which only sizes a button on a phone, so on a desktop
// "Cancel" rendered as bare text and "Save the rota" as a square green block touching it (the row
// also had no gap, which every other caller adds itself, as the contract above says). These are the
// two most common footer strings already in the app (font-menu text-xs, px-4 py-2), not new styling;
// the primary carries a border in its own colour so the pair are the same height. Hover is a
// background wash and a text colour only (the no-left-stripe rule).
export const MODAL_PRIMARY_BTN = `rounded-ele border border-ele-green bg-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50 ${MODAL_ACTION_BTN}`;
export const MODAL_SECONDARY_BTN = `rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-inset hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`;
